/**
 * BillingSettingsPage - Global billing configuration
 *
 * Features:
 * - Tax rates configuration
 * - Default currency settings
 * - Payment gateway configuration
 * - Invoice settings
 * - Billing alerts configuration
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Settings, DollarSign, Bell, FileText, AlertTriangle, Save } from 'lucide-react';
import type { Json } from '@/integrations/supabase/types';
import { Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { BillingAlerts } from '@/components/admin/BillingAlerts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface BillingSettings {
  default_currency: string;
  default_tax_rate: number;
  invoice_prefix: string;
  invoice_footer: string | null;
  stripe_publishable_key: string | null;
  stripe_secret_key: string | null;
}

const SUPPORTED_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
];

export default function BillingSettingsPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  // Fetch billing settings
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['admin-billing-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calie_admin_get_billing_settings');
      if (error) throw error;
      // RPC returns array, get first row
      const row = Array.isArray(data) ? data[0] : data;
      return row as BillingSettings;
    },
    enabled: !authLoading && isAdmin(),
  });

  const { register, handleSubmit, reset, watch, setValue } = useForm<BillingSettings>({
    values: settings,
  });

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: async (data: BillingSettings) => {
      const { error } = await supabase.rpc('calie_admin_update_billing_settings', {
        _settings: data as unknown as Json,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-billing-settings'] });
      toast.success('Billing settings updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update settings: ${error.message}`);
    },
  });

  // Redirect non-admins
  if (!authLoading && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = (data: BillingSettings) => {
    updateMutation.mutate(data);
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-6 px-4 max-w-6xl">
        {/* Header */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Billing Settings
            </CardTitle>
            <CardDescription>
              Configure global billing settings, tax rates, and payment gateway integration
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="mb-6 border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                <p>Failed to load settings: {(error as Error).message}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Tabs */}
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="general" className="gap-2">
              <Settings className="h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="payment" className="gap-2">
              <DollarSign className="h-4 w-4" />
              Payment Gateway
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-2">
              <FileText className="h-4 w-4" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="alerts" className="gap-2">
              <Bell className="h-4 w-4" />
              Alerts
            </TabsTrigger>
          </TabsList>

          {/* General Settings Tab */}
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>General Billing Settings</CardTitle>
                <CardDescription>
                  Configure default currency and tax rates for the platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="currency">Default Currency</Label>
                      <Select
                        value={watch('default_currency')}
                        onValueChange={(value) => setValue('default_currency', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SUPPORTED_CURRENCIES.map((currency) => (
                            <SelectItem key={currency.code} value={currency.code}>
                              {currency.symbol} {currency.name} ({currency.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground">
                        Currency used for all billing transactions
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tax_rate">Default Tax Rate (%)</Label>
                      <Input
                        id="tax_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        {...register('default_tax_rate', { valueAsNumber: true })}
                      />
                      <p className="text-sm text-muted-foreground">
                        Default tax rate applied to invoices (can be overridden per tenant)
                      </p>
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" disabled={updateMutation.isPending}>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Gateway Tab */}
          <TabsContent value="payment">
            <Card>
              <CardHeader>
                <CardTitle>Stripe Configuration</CardTitle>
                <CardDescription>
                  Configure Stripe payment gateway integration
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="stripe_publishable">Stripe Publishable Key</Label>
                      <Input
                        id="stripe_publishable"
                        type="text"
                        placeholder="pk_live_..."
                        {...register('stripe_publishable_key')}
                      />
                      <p className="text-sm text-muted-foreground">
                        Your Stripe publishable key (starts with pk_)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="stripe_secret">Stripe Secret Key</Label>
                      <Input
                        id="stripe_secret"
                        type="password"
                        placeholder="sk_live_..."
                        {...register('stripe_secret_key')}
                      />
                      <p className="text-sm text-muted-foreground">
                        Your Stripe secret key (starts with sk_) - stored securely
                      </p>
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" disabled={updateMutation.isPending}>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoice Settings Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <CardTitle>Invoice Configuration</CardTitle>
                <CardDescription>
                  Customize invoice numbering and content
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-20 w-full" />
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="invoice_prefix">Invoice Number Prefix</Label>
                      <Input
                        id="invoice_prefix"
                        type="text"
                        placeholder="INV-"
                        {...register('invoice_prefix')}
                      />
                      <p className="text-sm text-muted-foreground">
                        Prefix for invoice numbers (e.g., "INV-" creates INV-00001)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="invoice_footer">Invoice Footer Text</Label>
                      <textarea
                        id="invoice_footer"
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Thank you for your business..."
                        {...register('invoice_footer')}
                      />
                      <p className="text-sm text-muted-foreground">
                        Optional footer text displayed on all invoices
                      </p>
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" disabled={updateMutation.isPending}>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <BillingAlerts />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
